import { useState, useEffect } from "react";
import { X, ChevronRight, Lightbulb, Sparkles, Heart, Calendar, Share, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

// Explainer steps for new users
const EXPLAINER_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Phygital",
    description: "Your personal preference wallet for beauty and lifestyle services. Let's take a quick tour!",
    icon: Sparkles,
    highlight: null
  },
  {
    id: "defaults",
    title: "Set Your Defaults",
    description: "Save your preferences for hair, makeup, nails, and events. Providers will see these when you share your wallet.",
    icon: Settings,
    highlight: "defaults-tab"
  },
  {
    id: "inspo",
    title: "Save Inspiration",
    description: "Upload photos or save links of looks you love. Tip: Save images from Instagram to your phone first, then upload here.",
    icon: Heart,
    highlight: "inspo-tab"
  },
  {
    id: "experiences",
    title: "Plan Experiences",
    description: "Create an experience for your next appointment. Link your inspo and share with your provider.",
    icon: Calendar,
    highlight: "plan-experience"
  },
  {
    id: "share",
    title: "Share Your Wallet",
    description: "When you're ready, share your preference wallet with any provider via link, QR code, or NFC tap!",
    icon: Share,
    highlight: "share-wallet"
  }
];

const ExplainerWidget = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasSeenExplainer, setHasSeenExplainer] = useState(false);

  useEffect(() => {
    // Tour disabled for now
    setHasSeenExplainer(true);
    return;
    
    // Check if user has seen the explainer before
    const seen = localStorage.getItem("phygital_explainer_seen");
    if (!seen) {
      // Delay showing the explainer to let the page load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      setHasSeenExplainer(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < EXPLAINER_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem("phygital_explainer_seen", "true");
    setIsVisible(false);
    setHasSeenExplainer(true);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem("phygital_explainer_seen", "true");
    setIsVisible(false);
    setHasSeenExplainer(true);
    onComplete?.();
  };

  // Floating tip button for users who have seen the explainer
  if (hasSeenExplainer && !isVisible) {
    return (
      <button
        onClick={() => {
          setCurrentStep(0);
          setIsVisible(true);
        }}
        className="fixed bottom-24 left-4 md:bottom-6 md:left-6 z-40 w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
        title="Show tips"
      >
        <Lightbulb className="w-5 h-5 text-white" />
      </button>
    );
  }

  if (!isVisible) return null;

  const step = EXPLAINER_STEPS[currentStep];
  const StepIcon = step.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={handleSkip} />
      
      {/* Modal */}
      <div className="fixed bottom-0 left-0 right-0 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-50 md:max-w-md w-full">
        <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-2xl overflow-hidden">
          {/* Progress */}
          <div className="flex gap-1 p-3 bg-gray-50">
            {EXPLAINER_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 flex-1 rounded-full transition-all ${
                  idx === currentStep ? "bg-pink-500" : idx < currentStep ? "bg-pink-200" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
          
          {/* Content */}
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <StepIcon className="w-8 h-8 text-pink-600" />
            </div>
            
            <h3 className="text-xl font-bold mb-2">{step.title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
            
            {/* Step indicator */}
            <p className="text-xs text-gray-400 mt-4">
              {currentStep + 1} of {EXPLAINER_STEPS.length}
            </p>
          </div>
          
          {/* Actions */}
          <div className="p-4 border-t border-gray-100 flex gap-3">
            {currentStep > 0 ? (
              <Button variant="outline" onClick={handlePrev} className="flex-1">
                Back
              </Button>
            ) : (
              <Button variant="ghost" onClick={handleSkip} className="flex-1 text-gray-500">
                Skip tour
              </Button>
            )}
            
            <Button onClick={handleNext} className="flex-1 bg-black hover:bg-gray-800">
              {currentStep === EXPLAINER_STEPS.length - 1 ? "Get Started" : "Next"}
              {currentStep < EXPLAINER_STEPS.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// Inline tip component for contextual hints
export const InlineTip = ({ tip, className = "" }) => {
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    const dismissedTips = JSON.parse(localStorage.getItem("phygital_dismissed_tips") || "[]");
    if (dismissedTips.includes(tip.id)) {
      setDismissed(true);
    }
  }, [tip.id]);
  
  const handleDismiss = () => {
    const dismissedTips = JSON.parse(localStorage.getItem("phygital_dismissed_tips") || "[]");
    localStorage.setItem("phygital_dismissed_tips", JSON.stringify([...dismissedTips, tip.id]));
    setDismissed(true);
  };
  
  if (dismissed) return null;
  
  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3 flex items-start gap-3 ${className}`}>
      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Lightbulb className="w-4 h-4 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-blue-900">{tip.text}</p>
      </div>
      <button
        onClick={handleDismiss}
        className="w-6 h-6 rounded-full hover:bg-blue-100 flex items-center justify-center flex-shrink-0"
      >
        <X className="w-3 h-3 text-blue-400" />
      </button>
    </div>
  );
};

// Predefined tips for different sections
export const TIPS = {
  defaults: {
    id: "tip-defaults",
    text: "Set your preferences here! Your hair length, favorite nail styles, makeup looks - providers will see these when you share."
  },
  inspo: {
    id: "tip-inspo",
    text: "Save inspiration photos by uploading from your device, saving from Pinterest, or browsing the inspiration gallery!"
  },
  experiences: {
    id: "tip-experiences",
    text: "Plan your next appointment by creating an experience. Link your inspo photos and share with your provider!"
  },
  share: {
    id: "tip-share",
    text: "Ready to share? Tap the Share button to send your preference wallet to any provider via link, QR code, or NFC."
  }
};

export default ExplainerWidget;
